import { CSS_CLASS } from '../config'
import { queryClosestParent } from './html'

/**
 * Get an image meta data
 *
 * @param {HTMLImageElement}    $img   - The image element.
 * @return {object}             The given image meta data
 */

const getImageMetadata = $img => ({
    url: $img.src,
    width: $img.naturalWidth,
    height: $img.naturalHeight,
    ratio: $img.naturalWidth / $img.naturalHeight,
})


/**
 * Load the given image.
 *
 * @param {string}  url         - The URI to lazy load into $el.
 * @param {object}  options     - An object of options
 * @return {void}
 */

const loadImage = (url, options = {}) => {
    return new Promise((resolve, reject) => {
        const $img = new Image();

        if (options.crossOrigin) {
            $img.crossOrigin = options.crossOrigin;
        }

        const loadCallback = () => {
            resolve({
                element: $img,
                ...getImageMetadata($img),
            });
        };

        if ($img.decode) {
            $img.src = url;
            $img.decode()
                .then(loadCallback)
                .catch((e) => {
                    reject(e);
                });
        } else {
            $img.onload = loadCallback;
            $img.onerror = (e) => {
                reject(e);
            };
            $img.src = url;
        }
    });
};

/**
 * Lazyload Callbacks
 *
 */
const lazyImageLoad = (e) => {
    const $img = e.currentTarget;
    const $parent = queryClosestParent($img, `.${CSS_CLASS.LAZY_CONTAINER}`);

    requestAnimationFrame(() => {
        if ($parent) {
            $parent.classList.remove(CSS_CLASS.LAZY_LOADING);
            $parent.classList.add(CSS_CLASS.LAZY_LOADED);
        }

        $img.classList.add(CSS_CLASS.LAZY_LOADED);
    });
};

const lazyImageError = (e) => {
    const $img = e.currentTarget;
    const $parent = queryClosestParent($img, `.${CSS_CLASS.LAZY_CONTAINER}`);

    requestAnimationFrame(() => {
        if ($parent) {
            $parent.classList.remove(CSS_CLASS.LAZY_LOADING);
            $parent.classList.add(CSS_CLASS.LAZY_ERROR);
        }
    });
};

/* Trigger Lazyload Callbacks */
const triggerLazyloadCallbacks = ($lazyImagesArgs) => {
    const $lazyImages = $lazyImagesArgs
        ? $lazyImagesArgs
        : document.querySelectorAll('[loading="lazy"]');

    if ("loading" in HTMLImageElement.prototype) {
        for (const $img of $lazyImages) {
            const $parent = queryClosestParent(
                $img,
                `.${CSS_CLASS.LAZY_CONTAINER}`
            );

            if (!$img.complete) {
                $parent?.classList.add(CSS_CLASS.LAZY_LOADING);
                $img.addEventListener("load", lazyImageLoad, { once: true });
                $img.addEventListener("error", lazyImageError, { once: true });
            } else {
                $parent.classList.add(CSS_CLASS.LAZY_LOADED);
                $img.classList.add(CSS_CLASS.LAZY_LOADED);
            }
        }
    } else {
        // if 'loading' supported
        for (const $img of $lazyImages) {
            const $parent = queryClosestParent(
                $img,
                `.${CSS_CLASS.LAZY_CONTAINER}`
            );
            $parent.classList.add(CSS_CLASS.LAZY_LOADED);
        }
    }
};

/* Reset Lazyload Callbacks */
const resetLazyloadCallbacks = () => {
    if ("loading" in HTMLImageElement.prototype) {
        const $lazyImages = document.querySelectorAll('[loading="lazy"]');
        for (const $img of $lazyImages) {
            $img.removeEventListener("load", lazyImageLoad, { once: true });
            $img.removeEventListener("error", lazyImageError, { once: true });
        }
    }
};

export { loadImage, triggerLazyloadCallbacks, resetLazyloadCallbacks };
